// Clothing, swimwear and body builds.
import * as THREE from 'three';
import { pick, rnd, rr } from '../../lib/random.js';
import { PEOPLE } from '../models.js';
import { prepared } from './prepared.js';

export let crowd, dress, outfit, swimOutfit, build, fatten;

export function buildOutfits() {
  // Re-dress a clone: its own colour buffer (other attributes shared), so nine base models make a varied crowd.
  // recolor(materialName, meshName) returns a Color, null to keep the original, or a function of the vertex's
  // bind-pose height returning a Color (to cut a garment, e.g. a bikini out of a top).
  dress = function dress(model, P, recolor) {
    model.traverse((o) => {
      if (!o.isSkinnedMesh) return;
      const src = o.geometry,
        g = new THREE.BufferGeometry(),
        col = src.attributes.color.array.slice();
      for (const k in src.attributes) if (k !== 'color') g.setAttribute(k, src.attributes[k]);
      for (const r of P.ranges) {
        const c = recolor(r.name, r.mesh);
        if (c)
          for (let i = r.start; i < r.start + r.count; i++) {
            const t = i - ((i - r.start) % 3);
            const k =
              typeof c === 'function' ? c((P.bindY[t] + P.bindY[t + 1] + P.bindY[t + 2]) / 3) : c;
            col.set([k.r, k.g, k.b], i * 3);
          }
      }
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      g.boundingSphere = src.boundingSphere;
      o.geometry = g;
    });
    return model;
  };
  // the crowd leaves out the women's adventurer model, whose giant backpack is baked into its body mesh
  crowd = prepared.filter((_, i) => i !== 8);
  const CLOTH = [
    0x2f3c55, 0x3a3a3a, 0x6b5a45, 0x1f2a40, 0xc9b99a, 0xd9534f, 0x3b7dd8, 0xf0c419, 0x2e9e6a,
    0xf2f0ea, 0x8e5bb5, 0xe67e22, 0xc2457a, 0x7a8f5a, 0x9aa5b1, 0x5a3e2b, 0xb8c9d9, 0x8a2c35,
  ];
  outfit = () => {
    const skin = new THREE.Color(pick(PEOPLE.skins)),
      hair = new THREE.Color(pick(PEOPLE.hairs)),
      cloth = {};
    const f = (name, mesh = '') => {
      if (name === 'Skin_Darker') return skin.clone().multiplyScalar(0.86);
      if (name === 'Skin') return skin;
      if (/^(Hair|Eyebrows|Moustache)/.test(name)) return hair;
      // anything else on the head (eyes, the women's brow material) keeps its colour
      if (name === 'Eye' || name === 'Gold' || /_Head_/.test(mesh)) return null;
      if (!(name in cloth)) cloth[name] = rnd() < 0.3 ? null : new THREE.Color(pick(CLOTH));
      return cloth[name];
    };
    f.skin = skin;
    return f;
  };
  // swimwear for the beach: the men's casual model (it wears shorts) goes shirtless and barefoot; the women's
  // casual model gets a bikini cut out of its top and trousers by height, bare everywhere else
  const SWIM = [0xd9412c, 0x1d2433, 0x2f6fd0, 0xf2c014, 0xe86aa6, 0x3fb7a6, 0x111111, 0xf28c28];
  swimOutfit = (female) => {
    const o = outfit(),
      suit = new THREE.Color(pick(SWIM));
    return (name, mesh) => {
      if (/_Head_/.test(mesh)) return o(name, mesh);
      if (female)
        return mesh === 'Casual_Body_1'
          ? (y) => (y > 1.25 ? suit : o.skin)
          : mesh === 'Casual_Legs'
            ? (y) => (y > 0.93 ? suit : o.skin)
            : o.skin;
      return mesh === 'Casual_Legs_2' ? suit : o.skin;
    };
  };
  // body build: [width, height] multipliers. Slim, average or heavy-set, heights from about 1.55 m to 1.92 m
  build = () => {
    const r = rnd(),
      ht = rr(0.89, 1.1);
    return r < 0.3
      ? [rr(0.88, 0.95), ht, 1]
      : r < 0.75
        ? [rr(0.96, 1.04), ht, 1]
        : [rr(1.03, 1.1), ht * 0.97, rr(1.14, 1.3)];
  };
  // heavy-set: a wider belly and chest on the abdomen bone, undone at the neck and shoulders so head and arms keep their shape
  fatten = (model, belly) => {
    if (belly <= 1) return;
    const b = (n) => model.getObjectByName(n);
    b('Abdomen').scale.set(belly, 1, belly * 1.06);
    for (const n of ['Neck', 'ShoulderL', 'ShoulderR'])
      b(n).scale.set(1 / belly, 1, 1 / (belly * 1.06));
  };
}
